import { ActionReducerMapBuilder, Draft, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import {
  BebyggelseobjektDomaner,
  ForvaltningsorganisationTyp,
  HuvudgruppTyp,
  HuvudkategoriTyp,
  ModelError,
  Skyddstyp,
  State
} from 'models';

import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from './storeHelpers';

import BebyggelseApi from 'api/bebyggelseApi';

export const getBebyggelseobjektDomaner = createAsyncThunk<
  BebyggelseobjektDomaner,
  undefined,
  { rejectValue: ModelError }
>('bebyggelseobjekt/domaner/fetch', async (_undefined: undefined, { rejectWithValue }) => {
  const result = await BebyggelseApi.fetchBebyggelseobjektDomaner();
  return errorOr(result, rejectWithValue);
});

const initialState: State<BebyggelseobjektDomaner> = {
  data: undefined,
  error: undefined,
  requestStatus: 'idle',
  requestType: undefined,
  currentRequestId: undefined
};

const bebyggelseobjektDomanerSlice = createSlice({
  name: 'domaner',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<State<BebyggelseobjektDomaner>>>) => {
    builder.addCase(
      getBebyggelseobjektDomaner.pending,
      (state: Draft<State<BebyggelseobjektDomaner>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      getBebyggelseobjektDomaner.fulfilled,
      (state: Draft<State<BebyggelseobjektDomaner>>, action: FulfilledActionType<BebyggelseobjektDomaner>): void => {
        action.payload = {
          forvaltningsorganisationer: action.payload.forvaltningsorganisationer.filter(
            (typ: ForvaltningsorganisationTyp) => typ.aktiv
          ),
          skyddstyper: action.payload.skyddstyper.filter((skydd: Skyddstyp) => skydd.aktiv),
          kategorityper: action.payload.kategorityper.map((huvudgrupp: HuvudgruppTyp) => ({
            ...huvudgrupp,
            huvudkategorier: huvudgrupp.huvudkategorier.map((huvudkategori: HuvudkategoriTyp) => ({
              ...huvudkategori,
              underkategorier: huvudkategori.underkategorier
            }))
          })),
          egenskaper: action.payload.egenskaper,
          upphovsmanrolltyper: action.payload.upphovsmanrolltyper,
          handelsetyper: action.payload.handelsetyper,
          hanvisningstyper: action.payload.hanvisningstyper,
          dokumentationlicenstyper: action.payload.dokumentationlicenstyper
        };
        fulfilled(state, action);
      }
    );
    builder.addCase(
      getBebyggelseobjektDomaner.rejected,
      (state: Draft<State<BebyggelseobjektDomaner>>, action: RejectedActionType): void => rejected(state, action)
    );
  }
});

export default bebyggelseobjektDomanerSlice.reducer;
